import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import type { CaseView } from "shared";
import { api } from "../api";

type Tab = "open" | "closed";
type CallbackReq = { id: string; state?: string; createdAt: string };

/** Pending web callback requests. Hidden when import is not configured (503) or on any failure; names and phones stay out of the list and URLs. */
function CallbackRequests() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["callbacks"], retry: false, queryFn: () => api<{ requests: CallbackReq[] }>("/api/callbacks") });
  const create = useMutation({
    mutationFn: (id: string) => api<{ case: { id: string } }>(`/api/callbacks/${encodeURIComponent(id)}/case`, { method: "POST" }),
    onSuccess: (r) => { void qc.invalidateQueries({ queryKey: ["callbacks"] }); void qc.invalidateQueries({ queryKey: ["cases"] }); navigate({ to: "/cases/$id", params: { id: r.case.id } }); },
  });
  const items = q.data?.requests ?? [];
  if (!items.length) return null;
  return (
    <section aria-labelledby="cb-h" className="mb-5">
      <h2 id="cb-h" className="mb-2 text-sm font-semibold">Web callback requests</h2>
      <ul className="divide-y divide-slate-200 rounded-lg border border-slate-200 bg-white dark:divide-slate-800 dark:border-slate-800 dark:bg-slate-900">
        {items.map((r) => (
          <li key={r.id} className="flex items-center gap-4 px-4 py-2 text-sm">
            <span>{new Date(r.createdAt).toLocaleString()}</span>
            <span className="text-slate-600 dark:text-slate-400">{r.state ?? "State not given"}</span>
            <button type="button" className="btn-primary ml-auto" disabled={create.isPending} onClick={() => create.mutate(r.id)}>Create case</button>
          </li>
        ))}
      </ul>
      {create.error && <p role="alert" className="mt-1 text-sm text-red-600">Could not create the case.</p>}
    </section>
  );
}

/** Case list: Open or Closed tab, a "Mine" filter, and a name or phone filter applied here so no customer detail goes into a URL. */
export function Cases() {
  const [tab, setTab] = useState<Tab>("open");
  const [mine, setMine] = useState(false);
  const [text, setText] = useState("");
  const scope = tab === "closed" ? "closed" : mine ? "mine" : "open";
  const q = useQuery({ queryKey: ["cases", scope], queryFn: () => api<{ cases: CaseView[] }>(`/api/cases?scope=${scope}`) });
  const needle = text.trim().toLowerCase();
  const digits = needle.replace(/\D/g, "");
  const rows = (q.data?.cases ?? []).filter((c) => !needle || c.customer.name.toLowerCase().includes(needle)
    || c.title.toLowerCase().includes(needle) || (digits.length > 0 && (c.customer.phone ?? "").replace(/\D/g, "").includes(digits)));
  return (
    <div className="mx-auto max-w-5xl">
      <div className="mb-4 flex items-center justify-between">
        <h1 className="text-xl font-semibold">Cases</h1>
        <Link to="/cases/new" className="btn-primary">New case</Link>
      </div>
      <CallbackRequests />
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <div role="tablist" className="flex gap-1">
          {(["open", "closed"] as const).map((t) => (
            <button key={t} type="button" role="tab" aria-selected={tab === t} onClick={() => setTab(t)}
              className={`btn ${tab === t ? "bg-brand-50 text-brand-700 dark:bg-slate-800 dark:text-white" : "text-slate-600"}`}>{t === "open" ? "Open" : "Closed"}</button>
          ))}
        </div>
        {tab === "open" && <label className="flex items-center gap-1.5 text-sm"><input type="checkbox" checked={mine} onChange={(e) => setMine(e.target.checked)} /> Mine</label>}
        <input aria-label="Filter cases" className="input max-w-xs" placeholder="Filter by name, phone or title" value={text} onChange={(e) => setText(e.target.value)} />
      </div>
      {q.isLoading && <p className="text-sm text-slate-500">Loading…</p>}
      {q.error && <p role="alert" className="text-sm text-red-600">Could not load cases.</p>}
      {q.data && rows.length === 0 && <p className="text-sm text-slate-500">No cases here.</p>}
      <ul className="divide-y divide-slate-200 rounded-lg border border-slate-200 bg-white dark:divide-slate-800 dark:border-slate-800 dark:bg-slate-900">
        {rows.map((c) => (
          <li key={c.id}>
            <Link to="/cases/$id" params={{ id: c.id }} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3 text-sm hover:bg-slate-50 dark:hover:bg-slate-800">
              <span className="font-medium">{c.title}</span>
              <span className="text-slate-600 dark:text-slate-400">{c.customer.name}</span>
              {(c.urgency === "high" || c.urgency === "emergency") && <span className="rounded bg-red-100 px-1.5 text-xs font-semibold text-red-700">{c.urgency}</span>}
              <span className="ml-auto text-xs text-slate-500">{c.status} · {c.owner.name} · {new Date(c.createdAt).toLocaleDateString()}</span>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
